import { useState } from 'react';
import { ChartDataResponse, DailyDataResponse, SearchForm, DailySearchForm, UserSearchForm, UserSearchResult, ApiError } from '../types';
import { ApiService } from '../services/api';

export type ViewType = 'empty' | 'period' | 'daily' | 'usersearch';

type UseSleepDataReturn = {
  chartData: ChartDataResponse | null;
  dailyData: DailyDataResponse | null;
  userSearchResults: UserSearchResult[] | null;
  userSearchParams: Omit<UserSearchForm, 'name' | 'email'> & { name?: string; email?: string } | null;
  viewType: ViewType;
  isLoading: boolean;
  error: string | null;
  handlePeriodSearch: (form: SearchForm) => Promise<void>;
  handleDailySearch: (form: DailySearchForm) => Promise<void>;
  handleUserSearch: (form: UserSearchForm) => Promise<void>;
  setError: (error: string | null) => void;
};

export const useSleepData = (): UseSleepDataReturn => {
  const [chartData, setChartData] = useState<ChartDataResponse | null>(null);
  const [dailyData, setDailyData] = useState<DailyDataResponse | null>(null);
  const [userSearchResults, setUserSearchResults] = useState<UserSearchResult[] | null>(null);
  const [userSearchParams, setUserSearchParams] = useState<Omit<UserSearchForm, 'name' | 'email'> & { name?: string; email?: string } | null>(null);
  const [viewType, setViewType] = useState<ViewType>('empty');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePeriodSearch = async (form: SearchForm) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const userId = parseInt(form.userId);
      const data = await ApiService.getChartData(userId, form.startDate, form.endDate);
      setChartData(data);
      setDailyData(null);
      setViewType('period');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'データの取得に失敗しました');
      setChartData(null);
      setViewType('empty');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDailySearch = async (form: DailySearchForm) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const userId = parseInt(form.userId);
      const data = await ApiService.getDailyData(userId, form.selectedDate);
      setDailyData(data);
      setChartData(null);
      setViewType('daily');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'データの取得に失敗しました');
      setDailyData(null);
      setViewType('empty');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserSearch = async (form: UserSearchForm) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await ApiService.searchUsers(form.name, form.email, form.startDate, form.endDate);
      setUserSearchResults(response.users);
      setUserSearchParams({
        name: form.name || undefined,
        email: form.email || undefined,
        startDate: form.startDate,
        endDate: form.endDate
      });
      setChartData(null);
      setDailyData(null);
      setViewType('usersearch');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'ユーザー検索に失敗しました');
      setUserSearchResults(null);
      setViewType('empty');
    } finally {
      setIsLoading(false);
    }
  };

  return {
    chartData,
    dailyData,
    userSearchResults,
    userSearchParams,
    viewType,
    isLoading,
    error,
    handlePeriodSearch,
    handleDailySearch,
    handleUserSearch,
    setError,
  };
};
